import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Database, HardDrive, FileText, AlertTriangle, CheckCircle, RefreshCw, TrendingUp, Eye, Edit, Trash2, X, Search, ChevronLeft, ChevronRight, Settings, Zap, ImageOff, Cloud, UploadCloud, Image as ImageIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AdminLayout } from "./AdminLayout";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDatabaseStats() {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Collection viewer state
  const [viewingCollection, setViewingCollection] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [docPage, setDocPage] = useState(1);
  const [docTotal, setDocTotal] = useState(0);
  const [docTotalPages, setDocTotalPages] = useState(0);
  const [docSearch, setDocSearch] = useState("");
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  
  // Edit modal state
  const [editingDoc, setEditingDoc] = useState(null);
  const [editData, setEditData] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  
  // Delete state
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Optimization state
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizeResult, setOptimizeResult] = useState(null);
  const [isRemoving, setIsRemoving] = useState(false);

  // Object storage state
  const [objStats, setObjStats] = useState(null);
  const [migration, setMigration] = useState(null);
  const [isMigrating, setIsMigrating] = useState(false);

  const fetchStats = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/database-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Failed to fetch database stats:", err);
      toast.error("Gagal memuat statistik database");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchObjStats();
  }, []);

  const fetchObjStats = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const res = await axios.get(`${API}/admin/object-storage-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setObjStats(res.data);
    } catch (err) {
      console.error("Failed to fetch object storage stats:", err);
    }
  };

  const pollMigration = () => {
    const token = localStorage.getItem("admin_token");
    const tick = async () => {
      try {
        const res = await axios.get(`${API}/admin/photo-migration/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMigration(res.data);
        if (res.data.running) {
          setTimeout(tick, 2000);
        } else {
          setIsMigrating(false);
          toast.success(`Migrasi selesai: ${res.data.migrated} foto dipindahkan, ${res.data.failed} gagal`);
          fetchStats();
          fetchObjStats();
        }
      } catch (err) {
        setIsMigrating(false);
      }
    };
    tick();
  };

  const startMigration = async () => {
    if (!confirm("Pindahkan SEMUA foto lama dari database ke Object Storage? Proses berjalan di latar belakang dan akan melegakan penyimpanan database.")) return;
    setIsMigrating(true);
    try {
      const token = localStorage.getItem("admin_token");
      const res = await axios.post(`${API}/admin/photo-migration/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      if (res.data.total > 0) {
        pollMigration();
      } else {
        setIsMigrating(false);
        fetchObjStats();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memulai migrasi");
      setIsMigrating(false);
    }
  };

  const formatBytesRaw = (bytes) => {
    if (!bytes) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    let i = 0;
    let n = bytes;
    while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(2)} ${units[i]}`;
  };

  // Fetch documents for a collection
  const fetchDocuments = async (collectionName, page = 1, search = "") => {
    setIsLoadingDocs(true);
    try {
      const token = localStorage.getItem("admin_token");
      const params = new URLSearchParams({ page, limit: 15 });
      if (search) params.append("search", search);
      
      const response = await axios.get(
        `${API}/admin/database/${collectionName}/documents?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setDocuments(response.data.documents);
      setDocTotal(response.data.total);
      setDocTotalPages(response.data.total_pages);
      setDocPage(response.data.page);
    } catch (err) {
      console.error("Failed to fetch documents:", err);
      toast.error("Gagal memuat dokumen");
    } finally {
      setIsLoadingDocs(false);
    }
  };

  // Open collection viewer
  const openCollectionViewer = (collectionName) => {
    setViewingCollection(collectionName);
    setDocSearch("");
    setSelectedDocs([]);
    fetchDocuments(collectionName, 1, "");
  };

  // Handle search
  const handleDocSearch = () => {
    fetchDocuments(viewingCollection, 1, docSearch);
  };

  // Handle pagination
  const handleDocPageChange = (newPage) => {
    fetchDocuments(viewingCollection, newPage, docSearch);
  };

  // Open edit modal
  const openEditModal = (doc) => {
    setEditingDoc(doc);
    setEditData(JSON.stringify(doc, null, 2));
  };

  // Save document edit
  const saveDocument = async () => {
    setIsSaving(true);
    try {
      const parsedData = JSON.parse(editData);
      const token = localStorage.getItem("admin_token");
      
      await axios.put(
        `${API}/admin/database/${viewingCollection}/documents/${editingDoc._id}`,
        parsedData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Dokumen berhasil diupdate");
      setEditingDoc(null);
      fetchDocuments(viewingCollection, docPage, docSearch);
      fetchStats(); // Refresh stats
    } catch (err) {
      if (err instanceof SyntaxError) {
        toast.error("Format JSON tidak valid");
      } else {
        toast.error("Gagal menyimpan dokumen");
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Delete single document
  const deleteDocument = async (docId) => {
    if (!confirm("Yakin ingin menghapus dokumen ini?")) return;
    
    setIsDeleting(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(
        `${API}/admin/database/${viewingCollection}/documents/${docId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Dokumen berhasil dihapus");
      fetchDocuments(viewingCollection, docPage, docSearch);
      fetchStats();
    } catch (err) {
      toast.error("Gagal menghapus dokumen");
    } finally {
      setIsDeleting(false);
    }
  };

  // Delete selected documents
  const deleteSelectedDocuments = async () => {
    if (selectedDocs.length === 0) {
      toast.error("Pilih dokumen yang akan dihapus");
      return;
    }
    
    if (!confirm(`Yakin ingin menghapus ${selectedDocs.length} dokumen terpilih?`)) return;
    
    setIsDeleting(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(
        `${API}/admin/database/${viewingCollection}/delete-many`,
        { ids: selectedDocs },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${selectedDocs.length} dokumen berhasil dihapus`);
      setSelectedDocs([]);
      fetchDocuments(viewingCollection, 1, docSearch);
      fetchStats();
    } catch (err) {
      toast.error("Gagal menghapus dokumen");
    } finally {
      setIsDeleting(false);
    }
  };

  // Toggle document selection
  const toggleDocSelection = (docId) => {
    setSelectedDocs(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  // Toggle all documents selection
  const toggleSelectAll = () => {
    if (selectedDocs.length === documents.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(documents.map(d => d._id));
    }
  };

  // Optimize payment_proof images (compress)
  const handleOptimize = async () => {
    if (!confirm("Proses ini akan mengompres semua gambar bukti pembayaran di database. Lanjutkan?")) return;
    
    setIsOptimizing(true);
    setOptimizeResult(null);
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.post(`${API}/admin/optimize-payments`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000
      });
      setOptimizeResult(response.data);
      toast.success(response.data.message);
      fetchStats(); // Refresh stats to show new sizes
    } catch (err) {
      console.error("Optimization failed:", err);
      toast.error("Gagal mengoptimalkan database");
    } finally {
      setIsOptimizing(false);
    }
  };

  // Remove payment proofs from confirmed payments
  const handleRemoveProofs = async (mode) => {
    const modeText = mode === "confirmed_only" 
      ? "yang sudah dikonfirmasi" 
      : "SEMUA";
    
    if (!confirm(`Hapus bukti pembayaran dari ${modeText} transaksi? Gambar tidak bisa dikembalikan!`)) return;
    
    setIsRemoving(true);
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.post(`${API}/admin/remove-payment-proofs`, 
        { mode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchStats();
    } catch (err) {
      toast.error("Gagal menghapus bukti pembayaran");
    } finally {
      setIsRemoving(false);
    }
  };

  // Format field value for display
  const formatFieldValue = (value) => {
    if (value === null || value === undefined) return <span className="text-slate-400">null</span>;
    if (typeof value === "boolean") return value ? "Ya" : "Tidak";
    if (typeof value === "object") return JSON.stringify(value).substring(0, 50) + "...";
    if (typeof value === "string" && value.length > 50) return value.substring(0, 50) + "...";
    return String(value);
  };

  // Get important fields to show in table
  const getDisplayFields = (collectionName) => {
    if (collectionName === "payments") {
      return ["no_samb", "nama", "jumlah", "status", "payment_date"];
    }
    if (collectionName === "topup_requests") {
      return ["user_id", "amount", "status", "created_at"];
    }
    return ["_id"];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "critical": return "text-red-600 bg-red-100";
      case "warning": return "text-amber-600 bg-amber-100";
      default: return "text-emerald-600 bg-emerald-100";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "critical": return <AlertTriangle className="w-5 h-5" />;
      case "warning": return <AlertTriangle className="w-5 h-5" />;
      default: return <CheckCircle className="w-5 h-5" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "critical": return "Kritis - Segera bersihkan data!";
      case "warning": return "Peringatan - Database hampir penuh";
      default: return "Sehat";
    }
  };

  const formatBytes = (mb) => {
    if (mb < 1) return `${(mb * 1024).toFixed(2)} KB`;
    return `${mb.toFixed(2)} MB`;
  };

  const getUsagePercentage = () => {
    if (!stats) return 0;
    // Assuming 512MB limit
    const limit = 512;
    return Math.min((stats.summary.total_size_mb / limit) * 100, 100);
  };

  return (
    <AdminLayout title="Database Monitor">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Database className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-slate-800">Database Monitor</h1>
              <p className="text-sm text-slate-500">Pantau penggunaan database produksi</p>
            </div>
          </div>
          <Button 
            onClick={fetchStats} 
            disabled={isLoading}
            className="gap-2"
            data-testid="refresh-stats-btn"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {isLoading && !stats ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : stats && (
          <>
            {/* Status Banner */}
            <Card className={`mb-6 border-2 ${
              stats.status === 'critical' ? 'border-red-300 bg-red-50' :
              stats.status === 'warning' ? 'border-amber-300 bg-amber-50' :
              'border-emerald-300 bg-emerald-50'
            }`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getStatusColor(stats.status)}`}>
                      {getStatusIcon(stats.status)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">Status Database: {getStatusText(stats.status)}</h3>
                      <p className="text-sm text-slate-600">
                        Database: <span className="font-mono font-medium">{stats.database_name}</span>
                      </p>
                    </div>
                  </div>
                  {lastUpdated && (
                    <p className="text-xs text-slate-500">
                      Update: {lastUpdated.toLocaleString("id-ID")}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Usage Bar */}
            <Card className="mb-6">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-violet-600" />
                  Penggunaan Storage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Total Digunakan</span>
                    <span className="font-semibold text-slate-800">
                      {formatBytes(stats.summary.total_size_mb)} / 512 MB
                    </span>
                  </div>
                  <div className="w-full h-4 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${
                        stats.status === 'critical' ? 'bg-red-500' :
                        stats.status === 'warning' ? 'bg-amber-500' :
                        'bg-emerald-500'
                      }`}
                      style={{ width: `${getUsagePercentage()}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>0 MB</span>
                    <span className="text-amber-600">Peringatan: {stats.thresholds.warning_mb} MB</span>
                    <span className="text-red-600">Kritis: {stats.thresholds.critical_mb} MB</span>
                    <span>512 MB</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card className="bg-gradient-to-br from-violet-50 to-white border-violet-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                      <Database className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <p className="text-xs text-violet-600">Collections</p>
                      <p className="text-xl font-heading font-bold text-violet-700">
                        {stats.summary.total_collections}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-blue-600">Documents</p>
                      <p className="text-xl font-heading font-bold text-blue-700">
                        {stats.summary.total_documents.toLocaleString("id-ID")}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <HardDrive className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-emerald-600">Data Size</p>
                      <p className="text-xl font-heading font-bold text-emerald-700">
                        {formatBytes(stats.summary.data_size_mb)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs text-amber-600">Index Size</p>
                      <p className="text-xl font-heading font-bold text-amber-700">
                        {formatBytes(stats.summary.index_size_mb)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Object Storage Emergent */}
            {objStats && (
              <Card className="mb-6 border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-white" data-testid="object-storage-card">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <p className="text-xs font-semibold tracking-wider text-teal-600 uppercase">Sistem &amp; Penyimpanan</p>
                      <CardTitle className="text-lg font-heading flex items-center gap-2 mt-1">
                        <Cloud className="w-5 h-5 text-teal-600" />
                        Object Storage Emergent — Foto Lapangan
                      </CardTitle>
                    </div>
                    <span className="text-sm font-bold px-3 py-1.5 rounded-lg bg-teal-500 text-white">
                      {objStats.usage_percent < 0.01 ? "0.00" : objStats.usage_percent.toFixed(2)}% terpakai
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="w-full h-4 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-teal-500 transition-all duration-500"
                      style={{ width: `${Math.max(Math.min(objStats.usage_percent, 100), objStats.used_bytes > 0 ? 1 : 0)}%` }} />
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Pemakaian</p>
                      <p className="text-lg font-heading font-bold text-slate-800" data-testid="obj-used">{formatBytesRaw(objStats.used_bytes)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Batas Kuota</p>
                      <p className="text-lg font-heading font-bold text-slate-800">{formatBytesRaw(objStats.quota_bytes)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Sisa</p>
                      <p className="text-lg font-heading font-bold text-emerald-600">{formatBytesRaw(objStats.remaining_bytes)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Jumlah Foto</p>
                      <p className="text-lg font-heading font-bold text-slate-800" data-testid="obj-count">{objStats.photo_count.toLocaleString("id-ID")}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Rata-rata Ukuran</p>
                      <p className="text-sm font-medium text-slate-700">{formatBytesRaw(objStats.avg_size_bytes)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Foto Terbesar</p>
                      <p className="text-sm font-medium text-slate-700">{formatBytesRaw(objStats.largest_bytes)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Upload Pertama</p>
                      <p className="text-sm font-medium text-slate-700">{objStats.first_upload ? new Date(objStats.first_upload).toLocaleString("id-ID") : "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Upload Terakhir</p>
                      <p className="text-sm font-medium text-slate-700">{objStats.last_upload ? new Date(objStats.last_upload).toLocaleString("id-ID") : "-"}</p>
                    </div>
                  </div>

                  <div className="text-xs text-slate-500 flex items-start gap-1.5 pt-1">
                    <UploadCloud className="w-4 h-4 text-teal-500 shrink-0 mt-0.5" />
                    <span>Batas {formatBytesRaw(objStats.quota_bytes)} default Emergent Object Storage. Maks. per file: <strong>{formatBytesRaw(objStats.max_file_bytes)}</strong>. Foto baru otomatis tersimpan ke Object Storage.</span>
                  </div>

                  {/* Migration */}
                  <div className="p-4 rounded-xl border border-teal-200 bg-white">
                    <div className="flex items-center gap-2 mb-1">
                      <ImageIcon className="w-5 h-5 text-teal-600" />
                      <h4 className="font-semibold text-slate-800">Migrasi Foto Lama (Database → Object Storage)</h4>
                    </div>
                    <p className="text-xs text-slate-600 mb-3">
                      Pindahkan foto rumah pelanggan yang masih tersimpan sebagai base64 di database ke Object Storage untuk melegakan penyimpanan.
                      {" "}Foto belum dipindahkan: <strong className="text-rose-600" data-testid="pending-b64-count">{objStats.pending_base64.toLocaleString("id-ID")}</strong>
                    </p>
                    {migration && migration.running && (
                      <div className="mb-3">
                        <div className="flex justify-between text-xs text-slate-600 mb-1">
                          <span>Memproses {migration.processed}/{migration.total} ({migration.migrated} dipindahkan)</span>
                          <span>{migration.total ? Math.round((migration.processed / migration.total) * 100) : 0}%</span>
                        </div>
                        <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${migration.total ? (migration.processed / migration.total) * 100 : 0}%` }} />
                        </div>
                      </div>
                    )}
                    <Button
                      onClick={startMigration}
                      disabled={isMigrating || objStats.pending_base64 === 0}
                      className="gap-2 bg-teal-600 hover:bg-teal-700"
                      data-testid="start-migration-btn"
                    >
                      {isMigrating ? (
                        <><RefreshCw className="w-4 h-4 animate-spin" /> Memindahkan...</>
                      ) : objStats.pending_base64 === 0 ? (
                        <><CheckCircle className="w-4 h-4" /> Semua Foto Sudah di Object Storage</>
                      ) : (
                        <><UploadCloud className="w-4 h-4" /> Migrasi {objStats.pending_base64.toLocaleString("id-ID")} Foto Sekarang</>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Collections Table */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Database className="w-5 h-5 text-violet-600" />
                  Detail Per Collection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left p-3 font-semibold text-slate-600">Collection</th>
                        <th className="text-right p-3 font-semibold text-slate-600">Documents</th>
                        <th className="text-right p-3 font-semibold text-slate-600">Size</th>
                        <th className="text-right p-3 font-semibold text-slate-600">Avg Doc Size</th>
                        <th className="text-right p-3 font-semibold text-slate-600">Indexes</th>
                        <th className="text-center p-3 font-semibold text-slate-600">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.collections.map((coll, index) => (
                        <tr key={coll.name} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-3">
                            <span className={`font-mono px-2 py-1 rounded ${
                              coll.manageable 
                                ? 'text-emerald-600 bg-emerald-50 cursor-pointer hover:bg-emerald-100' 
                                : 'text-violet-600 bg-violet-50'
                            }`}
                            onClick={() => coll.manageable && openCollectionViewer(coll.name)}
                            >
                              {coll.name}
                              {coll.manageable && <Settings className="w-3 h-3 inline ml-1" />}
                            </span>
                          </td>
                          <td className="p-3 text-right font-medium text-slate-800">
                            {coll.documents.toLocaleString("id-ID")}
                          </td>
                          <td className="p-3 text-right text-slate-600">
                            {formatBytes(coll.size_mb)}
                          </td>
                          <td className="p-3 text-right text-slate-500 text-xs">
                            {coll.avg_doc_size ? `${coll.avg_doc_size.toFixed(0)} bytes` : '-'}
                          </td>
                          <td className="p-3 text-right text-slate-500">
                            {coll.indexes || '-'}
                          </td>
                          <td className="p-3 text-center">
                            {coll.manageable ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openCollectionViewer(coll.name)}
                                className="gap-1 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                                data-testid={`manage-${coll.name}-btn`}
                              >
                                <Settings className="w-4 h-4" />
                                Kelola
                              </Button>
                            ) : (
                              <span className="text-xs text-slate-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50 font-semibold">
                      <tr>
                        <td className="p-3 text-slate-600">TOTAL</td>
                        <td className="p-3 text-right text-slate-800">
                          {stats.summary.total_documents.toLocaleString("id-ID")}
                        </td>
                        <td className="p-3 text-right text-violet-600">
                          {formatBytes(stats.summary.data_size_mb)}
                        </td>
                        <td className="p-3"></td>
                        <td className="p-3"></td>
                        <td className="p-3"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Optimization Tools */}
            <Card className="mt-6 border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Zap className="w-5 h-5 text-orange-600" />
                  Optimasi Database
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-600">
                  Bukti pembayaran (gambar) yang disimpan di database membuat ukuran sangat besar. 
                  Gunakan tools di bawah untuk mengoptimalkan.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Compress Option */}
                  <div className="p-4 rounded-xl border border-blue-200 bg-blue-50/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-5 h-5 text-blue-600" />
                      <h4 className="font-semibold text-blue-800">Kompres Gambar</h4>
                    </div>
                    <p className="text-xs text-blue-600 mb-3">
                      Kompres semua gambar bukti pembayaran tanpa menghapusnya. 
                      Bisa menghemat 60-80% ukuran.
                    </p>
                    <Button
                      onClick={handleOptimize}
                      disabled={isOptimizing}
                      className="w-full gap-2 bg-blue-600 hover:bg-blue-700"
                      data-testid="optimize-payments-btn"
                    >
                      {isOptimizing ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Mengompres...
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4" />
                          Kompres Semua Gambar
                        </>
                      )}
                    </Button>
                    {optimizeResult && (
                      <div className="mt-3 p-2 rounded-lg bg-blue-100 text-xs text-blue-700">
                        Berhasil: {optimizeResult.optimized} gambar dikompres, 
                        hemat {optimizeResult.total_saved_mb} MB
                      </div>
                    )}
                  </div>

                  {/* Remove Option */}
                  <div className="p-4 rounded-xl border border-red-200 bg-red-50/50">
                    <div className="flex items-center gap-2 mb-2">
                      <ImageOff className="w-5 h-5 text-red-600" />
                      <h4 className="font-semibold text-red-800">Hapus Bukti Pembayaran</h4>
                    </div>
                    <p className="text-xs text-red-600 mb-3">
                      Hapus gambar bukti pembayaran dari transaksi yang sudah dikonfirmasi. 
                      Data transaksi tetap aman.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleRemoveProofs("confirmed_only")}
                        disabled={isRemoving}
                        variant="outline"
                        className="flex-1 gap-1 text-red-600 border-red-200 hover:bg-red-100 text-xs"
                        data-testid="remove-confirmed-proofs-btn"
                      >
                        {isRemoving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <ImageOff className="w-3 h-3" />}
                        Konfirmasi Saja
                      </Button>
                      <Button
                        onClick={() => handleRemoveProofs("all")}
                        disabled={isRemoving}
                        variant="destructive"
                        className="flex-1 gap-1 text-xs"
                        data-testid="remove-all-proofs-btn"
                      >
                        {isRemoving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                        Hapus Semua
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tips */}
            <Card className="mt-6 bg-gradient-to-br from-slate-50 to-white">
              <CardContent className="p-4">
                <h4 className="font-semibold text-slate-800 mb-2">Tips Menghemat Storage:</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• <strong>Kompres gambar</strong> — Gunakan tombol "Kompres Semua Gambar" untuk mengecilkan ukuran bukti pembayaran</li>
                  <li>• <strong>Hapus bukti lama</strong> — Hapus gambar bukti dari transaksi yang sudah dikonfirmasi</li>
                  <li>• Hapus transaksi lama yang tidak diperlukan dari menu <strong>Kelola Transaksi</strong></li>
                  <li>• Arsipkan data pembayaran lama ke file Excel sebelum menghapus</li>
                  <li>• Upload gambar baru otomatis dikompres (max 800px, JPEG 50%)</li>
                  <li>• Peringatan akan muncul saat penggunaan mencapai {stats.thresholds.warning_mb} MB</li>
                </ul>
              </CardContent>
            </Card>
          </>
        )}

        {/* Collection Viewer Modal */}
        {viewingCollection && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setViewingCollection(null)}>
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative w-full max-w-6xl max-h-[90vh] bg-white rounded-2xl overflow-hidden flex flex-col" 
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-emerald-500 to-teal-600 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                    <Database className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-white">
                      Kelola Collection: <span className="font-mono">{viewingCollection}</span>
                    </h3>
                    <p className="text-sm text-white/80">{docTotal} dokumen</p>
                  </div>
                </div>
                <button
                  onClick={() => setViewingCollection(null)}
                  className="w-10 h-10 rounded-full hover:bg-white/20 flex items-center justify-center"
                >
                  <X className="w-6 h-6 text-white" />
                </button>
              </div>

              {/* Search & Actions */}
              <div className="p-4 border-b border-slate-200 bg-slate-50 flex flex-wrap gap-3 items-center justify-between">
                <div className="flex gap-2 items-center flex-1 min-w-[200px]">
                  <div className="relative flex-1 max-w-xs">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Cari..."
                      value={docSearch}
                      onChange={(e) => setDocSearch(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleDocSearch()}
                      className="pl-9 h-9"
                      data-testid="doc-search-input"
                    />
                  </div>
                  <Button size="sm" onClick={handleDocSearch} data-testid="doc-search-btn">
                    Cari
                  </Button>
                </div>
                
                {selectedDocs.length > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={deleteSelectedDocuments}
                    disabled={isDeleting}
                    className="gap-1"
                    data-testid="delete-selected-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                    Hapus {selectedDocs.length} Terpilih
                  </Button>
                )}
              </div>

              {/* Documents Table */}
              <div className="flex-1 overflow-auto p-4">
                {isLoadingDocs ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="spinner" />
                  </div>
                ) : documents.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>Tidak ada dokumen ditemukan</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                          <th className="p-2 w-10">
                            <input
                              type="checkbox"
                              checked={selectedDocs.length === documents.length && documents.length > 0}
                              onChange={toggleSelectAll}
                              className="rounded border-slate-300"
                            />
                          </th>
                          <th className="text-left p-2 font-semibold text-slate-600 text-xs">ID</th>
                          {getDisplayFields(viewingCollection).map(field => (
                            <th key={field} className="text-left p-2 font-semibold text-slate-600 text-xs uppercase">
                              {field.replace(/_/g, ' ')}
                            </th>
                          ))}
                          <th className="text-center p-2 font-semibold text-slate-600 text-xs">Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documents.map((doc) => (
                          <tr key={doc._id} className={`border-b border-slate-100 hover:bg-slate-50 ${selectedDocs.includes(doc._id) ? 'bg-emerald-50' : ''}`}>
                            <td className="p-2">
                              <input
                                type="checkbox"
                                checked={selectedDocs.includes(doc._id)}
                                onChange={() => toggleDocSelection(doc._id)}
                                className="rounded border-slate-300"
                              />
                            </td>
                            <td className="p-2">
                              <span className="font-mono text-xs text-slate-500 bg-slate-100 px-1 py-0.5 rounded">
                                ...{doc._id.slice(-6)}
                              </span>
                            </td>
                            {getDisplayFields(viewingCollection).map(field => (
                              <td key={field} className="p-2 text-sm text-slate-700">
                                {formatFieldValue(doc[field])}
                              </td>
                            ))}
                            <td className="p-2">
                              <div className="flex justify-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditModal(doc)}
                                  className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-50"
                                  data-testid={`edit-doc-${doc._id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteDocument(doc._id)}
                                  disabled={isDeleting}
                                  className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                                  data-testid={`delete-doc-${doc._id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {docTotalPages > 1 && (
                <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
                  <p className="text-sm text-slate-600">
                    Halaman {docPage} dari {docTotalPages} ({docTotal} dokumen)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDocPageChange(docPage - 1)}
                      disabled={docPage === 1 || isLoadingDocs}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDocPageChange(docPage + 1)}
                      disabled={docPage === docTotalPages || isLoadingDocs}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}

        {/* Edit Document Modal */}
        {editingDoc && (
          <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" onClick={() => setEditingDoc(null)}>
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative w-full max-w-2xl max-h-[85vh] bg-white rounded-2xl overflow-hidden flex flex-col" 
              onClick={e => e.stopPropagation()}
            >
              {/* Edit Header */}
              <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                    <Edit className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-white">Edit Dokumen</h3>
                    <p className="text-sm text-white/80 font-mono">ID: {editingDoc._id}</p>
                  </div>
                </div>
                <button
                  onClick={() => setEditingDoc(null)}
                  className="w-10 h-10 rounded-full hover:bg-white/20 flex items-center justify-center"
                >
                  <X className="w-6 h-6 text-white" />
                </button>
              </div>

              {/* Edit Content */}
              <div className="flex-1 overflow-auto p-4">
                <p className="text-sm text-slate-600 mb-3">
                  Edit data dalam format JSON. Hati-hati saat mengubah field penting!
                </p>
                <textarea
                  value={editData}
                  onChange={(e) => setEditData(e.target.value)}
                  className="w-full h-[400px] font-mono text-sm p-4 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50"
                  spellCheck={false}
                  data-testid="edit-doc-textarea"
                />
              </div>

              {/* Edit Footer */}
              <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setEditingDoc(null)}
                >
                  Batal
                </Button>
                <Button
                  onClick={saveDocument}
                  disabled={isSaving}
                  className="gap-2"
                  data-testid="save-doc-btn"
                >
                  {isSaving ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Menyimpan...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Simpan
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </motion.div>
    </AdminLayout>
  );
}
